import jsPDF from 'jspdf';
import { LOGO_BASE64 } from './logoBase64';

// ── Colour palette ─────────────────────────────────────────────────────────
const GOLD       = '#C9A441';
const BLACK      = '#1A1A1A';
const GREY_TEXT  = '#333333';
const GREY_LIGHT = '#777777';
const ROW_ALT    = '#FDFAF3';

const PW = 210;
const PH = 297;
const M  = 14;
const CW = PW - M * 2;

const COMPANY_ADDRESS = 'Dutta Para Rd, Bangaon, West Bengal 743235';

function rupee(n: number): string {
  return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export type QuotationPdfData = {
  businessName: string;
  tagline?: string;
  address?: string;
  phone?: string;
  email?: string;
  gstNumber?: string;
  quotationNumber: string;
  date: Date;
  customerName: string;
  customerPhone: string;
  customerAddress?: string;
  items: Array<{
    itemName: string;
    sku: string;
    quantity: number;
    per?: string;
    price: number;
    total: number;
  }>;
  subtotal: number;
  deliveryCharge: number;
  discount: number;
  total: number;
  notes?: string;
};

export function generateQuotationPdfClient(
  data: QuotationPdfData,
  mode: 'download' | 'preview' | 'print' = 'download',
): string | void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ── Outer gold border ──────────────────────────────────────────────────────
  doc.setDrawColor(GOLD);
  doc.setLineWidth(1.2);
  doc.rect(6, 6, PW - 12, PH - 12);

  let y = M + 2;

  // ── 1. HEADER ─────────────────────────────────────────────────────────────
  // Logo top-left
  const logoSize = 18;
  try {
    doc.addImage(LOGO_BASE64, 'PNG', M, y, logoSize, logoSize);
  } catch { /* ignore */ }

  // Business name beside logo
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(BLACK);
  doc.text(data.businessName, M + logoSize + 3, y + 7);

  // Address below name
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(GREY_TEXT);
  const addressLines = doc.splitTextToSize(COMPANY_ADDRESS, CW * 0.55);
  doc.text(addressLines, M + logoSize + 3, y + 13);

  // Estimate No — top right
  const qNum = data.quotationNumber && data.quotationNumber !== 'DRAFT'
    ? data.quotationNumber : 'DRAFT';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(BLACK);
  doc.text(`Estimate No : ${qNum}`, PW - M, y + 4, { align: 'right' });

  // Date
  const dateStr = data.date.toLocaleDateString('en-IN', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(GREY_TEXT);
  doc.text(`Date: ${dateStr}`, PW - M, y + 11, { align: 'right' });

  y += logoSize + 4;

  // Gold divider
  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.6);
  doc.line(M, y, PW - M, y);
  y += 6;

  // ── 2. TO: CUSTOMER ────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(BLACK);
  doc.text(`To: ${data.customerName || '—'}`, M, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(GREY_TEXT);
  doc.text(`Ph. ${data.customerPhone || ''}`, M, y);

  if (data.customerAddress) {
    y += 4;
    doc.text(data.customerAddress, M, y);
  }

  y += 8;

  // ── 3. TABLE ───────────────────────────────────────────────────────────────
  const noW   = 10;
  const descW = 68;
  const qtyW  = 20;
  const perW  = 22;
  const rateW = 32;
  const amtW  = CW - noW - descW - qtyW - perW - rateW;

  const noX   = M;
  const descX = noX + noW;
  const qtyX  = descX + descW;
  const perX  = qtyX + qtyW;
  const rateX = perX + perW;
  const amtX  = rateX + rateW;

  const thH = 7.5;

  // Header — black fill, gold text
  doc.setFillColor(BLACK);
  doc.rect(noX, y, CW, thH, 'F');
  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.4);
  doc.rect(noX, y, CW, thH);
  doc.setLineWidth(0.2);
  doc.line(descX, y, descX, y + thH);
  doc.line(qtyX,  y, qtyX,  y + thH);
  doc.line(perX,  y, perX,  y + thH);
  doc.line(rateX, y, rateX, y + thH);
  doc.line(amtX,  y, amtX,  y + thH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(GOLD);
  doc.text('No',          noX   + noW / 2,        y + 5, { align: 'center' });
  doc.text('Description', descX + 2,               y + 5);
  doc.text('Quantity',    qtyX  + qtyW / 2,        y + 5, { align: 'center' });
  doc.text('Per',         perX  + perW / 2,        y + 5, { align: 'center' });
  doc.text('Rate',        rateX + rateW / 2,       y + 5, { align: 'center' });
  doc.text('Amount',      amtX  + amtW / 2,        y + 5, { align: 'center' });

  y += thH;

  // Item rows
  const rowH = 7;
  let totalQty = 0;

  data.items.forEach((item, i) => {
    if (y + rowH > PH - 55) {
      doc.addPage();
      doc.setDrawColor(GOLD);
      doc.setLineWidth(1.2);
      doc.rect(6, 6, PW - 12, PH - 12);
      y = M + 4;
    }

    // Alternating background
    if (i % 2 === 1) {
      doc.setFillColor(ROW_ALT);
      doc.rect(noX, y, CW, rowH, 'F');
    }

    // Row outer border — slightly thicker gold
    doc.setDrawColor(GOLD);
    doc.setLineWidth(0.4);
    doc.rect(noX, y, CW, rowH);
    // Inner column dividers — thinner
    doc.setLineWidth(0.2);
    doc.line(descX, y, descX, y + rowH);
    doc.line(qtyX,  y, qtyX,  y + rowH);
    doc.line(perX,  y, perX,  y + rowH);
    doc.line(rateX, y, rateX, y + rowH);
    doc.line(amtX,  y, amtX,  y + rowH);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(BLACK);

    doc.text(String(i + 1), noX + noW / 2, y + 4.8, { align: 'center' });

    let desc = item.itemName || '';
    while (doc.getTextWidth(desc) > descW - 4 && desc.length > 4) desc = desc.slice(0, -1);
    if (desc !== (item.itemName || '')) desc += '..';
    doc.text(desc, descX + 2, y + 4.8);

    doc.text(String(item.quantity), qtyX + qtyW / 2, y + 4.8, { align: 'center' });
    doc.text(item.per || 'Piece', perX + perW / 2, y + 4.8, { align: 'center' });
    doc.text(rupee(item.price), rateX + rateW - 2, y + 4.8, { align: 'right' });
    doc.text(rupee(item.total), amtX + amtW - 2,   y + 4.8, { align: 'right' });

    totalQty += item.quantity;
    y += rowH;
  });

  // Total qty row
  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.4);
  doc.rect(noX, y, CW, rowH);
  doc.setLineWidth(0.2);
  doc.line(descX, y, descX, y + rowH);
  doc.line(qtyX,  y, qtyX,  y + rowH);
  doc.line(perX,  y, perX,  y + rowH);
  doc.line(rateX, y, rateX, y + rowH);
  doc.line(amtX,  y, amtX,  y + rowH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(BLACK);
  doc.text(String(totalQty), qtyX + qtyW / 2, y + 4.8, { align: 'center' });
  y += rowH + 5;

  // ── 4. TOTALS ──────────────────────────────────────────────────────────────
  const labelX  = PW - M - 72;
  const valueX  = PW - M;
  const totRowH = 6.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  doc.setTextColor(GREY_LIGHT);
  doc.text('Sub Total :', labelX, y);
  doc.setTextColor(BLACK);
  doc.text(rupee(data.subtotal), valueX, y, { align: 'right' });
  y += totRowH;

  if (data.deliveryCharge > 0) {
    doc.setTextColor(GREY_LIGHT);
    doc.text('Delivery Charge :', labelX, y);
    doc.setTextColor(BLACK);
    doc.text(rupee(data.deliveryCharge), valueX, y, { align: 'right' });
    y += totRowH;
  }

  if (data.discount > 0) {
    doc.setTextColor(GREY_LIGHT);
    doc.text('Discount :', labelX, y);
    doc.setTextColor(BLACK);
    doc.text(`- ${rupee(data.discount)}`, valueX, y, { align: 'right' });
    y += totRowH;
  }

  // Gold divider before total
  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.6);
  doc.line(labelX, y, PW - M, y);
  y += 4;

  // Total row — gold background (contained within margins)
  doc.setFillColor('#FFF3CC');
  doc.rect(labelX - 2, y - 1, PW - M - labelX + 2, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(BLACK);
  doc.text('Total :', labelX, y + 5);
  doc.text(rupee(data.total), valueX, y + 5, { align: 'right' });
  y += 12;

  // ── 5. NOTES ──────────────────────────────────────────────────────────────
  if (data.notes?.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(GREY_LIGHT);
    const noteLines = doc.splitTextToSize(data.notes.trim(), CW);
    doc.text(noteLines, M, y);
  }

  // ── 6. FOOTER ─────────────────────────────────────────────────────────────
  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.4);
  doc.line(M, PH - 16, PW - M, PH - 16);

  doc.setFont('helvetica', 'bolditalic');
  doc.setFontSize(8.5);
  doc.setTextColor(BLACK);
  doc.text('Thank You For Your Business!', PW / 2, PH - 11, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(GREY_LIGHT);
  doc.text('Powered by NexaRise', PW / 2, PH - 7, { align: 'center' });

  // ── OUTPUT ────────────────────────────────────────────────────────────────
  if (mode === 'preview') {
    return doc.output('datauristring');
  }

  if (mode === 'print') {
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank', 'noopener,noreferrer');
    return;
  }

  const fileDateStr = data.date.toLocaleDateString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).replace(/\//g, '-');
  const safeName = (data.customerName || 'Quotation')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  doc.save(`Estimate_${safeName}_${fileDateStr}.pdf`);
}
